import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { enums } from '@kattana-io/common';
import { WatchlistWallet } from '../../../../reducers/wallet_watchlist';
import { GetWalletFromWatchlistData } from '../../../../selectors/watchlist';
import { createGetFromWalletTags } from '../../../../selectors/walletTags';
import { capitalize } from '../../../../components/Modal/TickersModal/helpers';
import Text from '../../../../atoms/Text';
import NumberDisplay from '../../../../molecules/NumberDisplay';

interface IWalletNicknameBalanceMobileProps {
  wallet: WatchlistWallet;
  id?: number;
  actionRemoveWatchlist?: (id: number) => void;
  actionAddWatchlist?: (
    data: GetWalletFromWatchlistData,
    tag?: enums.Tag
  ) => void;
}

const WalletNicknameBalanceMobile = ({
  wallet,
  id,
  actionRemoveWatchlist,
  actionAddWatchlist,
}: IWalletNicknameBalanceMobileProps) => {
  const selectFromWalletTags = useMemo(
    () => createGetFromWalletTags(wallet.address, wallet.chain),
    [wallet.address, wallet.chain]
  );

  const currentTag = useSelector(selectFromWalletTags);

  const ref = useRef(null);

  const isBigTradeWallet = 'value_usd' in wallet;
  const isActiveWallet = 'volume_24' in wallet;

  const onUnfav = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (ref.current && id) {
        (ref.current as HTMLElement).classList.add('watchlist__row--remove');
        setTimeout(() => {
          if (actionRemoveWatchlist) {
            actionRemoveWatchlist(id);
          }
        }, 150);
      }
    },
    [actionRemoveWatchlist, id]
  );

  const onFav = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (actionAddWatchlist) {
        const data: GetWalletFromWatchlistData =
          isBigTradeWallet || isActiveWallet
            ? { type: 'traders', chain: wallet.chain, address: wallet.address }
            : { type: 'overview', walletId: wallet.wallet_id };
        actionAddWatchlist(data);
      }
    },
    [actionAddWatchlist, wallet]
  );

  const nickname = capitalize(wallet.nickname);

  let itemValue: number;
  if (isActiveWallet) {
    itemValue = wallet.volume_24;
  } else if (isBigTradeWallet) {
    itemValue = wallet.value_usd;
  } else {
    itemValue = wallet.balance_usd;
  }

  return (
    <div ref={ref} className='ticker__cell'>
      {actionRemoveWatchlist && (
        <div
          className={`star star--${
            'tag' in wallet && wallet.tag ? wallet.tag : enums.Tag.Blue
          } icon-star-active`}
          onClick={onUnfav}
        />
      )}
      {actionAddWatchlist && (
        <div
          className='star tickers_footer_mobile__star--normal icon-star'
          data-qa='addFavourite'
          onClick={onFav}
        />
      )}
      <div className='name_exchange' style={{ maxWidth: 'calc(100% - 24px)' }}>
        <Text size={14} className='text--weight-500 text--grey-600'>
          {currentTag || nickname || 'Unnamed'}
        </Text>
        <div className='name_exchange__row'>
          {isBigTradeWallet && (
            <Text size={12} className='text--grey-400 text--weight-500'>
              Trade Value:
            </Text>
          )}
          {isActiveWallet && (
            <Text size={12} className='text--grey-400 text--weight-500'>
              Volume 24H:
            </Text>
          )}
          <NumberDisplay
            value={itemValue}
            round={isBigTradeWallet || isActiveWallet ? 1 : 2}
            startSymbol='$'
            textProps={{
              size: 14,
              className: 'text--grey-200',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default WalletNicknameBalanceMobile;
